/**
 * Premium Viewport Fade-in Effect
 *
 * Uses Intersection Observer to detect when images enter the viewport
 * and applies a staggered fade-in animation for a premium user experience.
 * Works with ALL images (lazy-loaded or not) for consistent fade-in across the site.
 */

class LazyLoadFade {
  constructor() {
    this.visibleCount = 0;
    this.staggerDelay = 100; // milliseconds between each image fade
    this.observedImages = new Set();
    this.intersectionObserver = null;

    // Target selectors for images that should fade in
    // NOTE: Product cards are excluded - they have instant loading for hover previews
    this.imageSelectors = [
      'img[loading="lazy"]:not(product-card img)',  // Lazy loaded images (except in product cards)
      '.product-media__image',                      // Product media gallery (product page)
      '.resource-image__image',                     // Resource images (collections, blog)
      '.blog-post-card__image',                     // Blog post cards
      '.image-block__image:not(product-card .image-block__image)',  // Image blocks (except product cards)
      '.collection-image__featured-image',          // Collection featured images
      '.sticky-add-to-cart__image-img',             // Sticky add to cart
      '.background-image-container img',            // Background images
    ];

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Create Intersection Observer for viewport detection
    this.createIntersectionObserver();

    // Observe initial images
    this.observeImages();

    // Re-observe when new content is added (e.g., infinite scroll, AJAX, quick view)
    this.observeNewContent();

    // Listen for variant changes (color/size changes on product pages)
    this.listenForVariantChanges();
  }

  createIntersectionObserver() {
    // Intersection Observer configuration
    const options = {
      root: null, // viewport
      rootMargin: '50px', // trigger slightly before entering viewport
      threshold: 0.01 // trigger when 1% visible (more sensitive)
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only process when entering viewport
        if (entry.isIntersecting) {
          const img = entry.target;

          // Stop observing this image
          this.intersectionObserver.unobserve(img);

          // Check if image is loaded
          if (img.complete && img.naturalHeight !== 0) {
            // Image already loaded - fade in with stagger
            this.fadeInImage(img);
          } else {
            // Wait for image to load, then fade in
            img.addEventListener('load', () => this.fadeInImage(img), { once: true });
            img.addEventListener('error', () => this.fadeInImage(img), { once: true });
          }
        }
      });
    }, options);
  }

  observeImages() {
    // Find all images matching our selectors
    const allImages = new Set();

    this.imageSelectors.forEach(selector => {
      const images = document.querySelectorAll(selector);
      images.forEach(img => allImages.add(img));
    });

    allImages.forEach(img => {
      // Skip if already observed
      if (this.observedImages.has(img)) return;

      // Skip SVGs and tiny images (likely icons)
      if (img.tagName !== 'IMG' || img.src?.includes('.svg') || img.naturalWidth < 50) {
        return;
      }

      this.observedImages.add(img);

      // Mobile fallback: If image is already loaded when script runs, immediately show it
      // This prevents blank images on mobile where timing can be different
      if (img.complete && img.naturalHeight !== 0) {
        const rect = img.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

        if (isInViewport) {
          // Image already loaded and visible - show it immediately
          img.classList.add('loaded');
          return;
        }
      }

      // Special handling for carousel/slideshow images (excluding product cards)
      const isInCarousel = img.closest('slideshow-component, slideshow-slide, [data-carousel-card]');
      const isInProductCard = img.closest('product-card');

      if (isInCarousel && !isInProductCard) {
        // Regular carousel images - check if the CAROUSEL is in viewport
        const carousel = img.closest('slideshow-component') || img.closest('[data-carousel-card]').parentElement;
        const carouselRect = carousel ? carousel.getBoundingClientRect() : null;
        const carouselInViewport = carouselRect &&
          carouselRect.top < window.innerHeight &&
          carouselRect.bottom > 0;

        if (carouselInViewport) {
          // Carousel is visible - force load ALL carousel images by removing lazy attribute
          // This fixes white flashes caused by browser not loading lazy images with aria-hidden
          const allCarouselImages = carousel.querySelectorAll('img');
          allCarouselImages.forEach(carouselImg => {
            if (carouselImg.hasAttribute('loading')) {
              carouselImg.removeAttribute('loading');
            }
          });

          // Now fade in this specific image with stagger
          if (img.complete && img.naturalHeight !== 0) {
            this.fadeInImage(img);
          } else {
            img.addEventListener('load', () => this.fadeInImage(img), { once: true });
            img.addEventListener('error', () => this.fadeInImage(img), { once: true });
          }
        } else {
          // Carousel not visible yet - observe it
          this.intersectionObserver.observe(img);
        }
      } else if (!isInProductCard) {
        // Regular images (not in carousel)
        const rect = img.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

        if (isInViewport) {
          // Already visible - check if it's a product page hero image
          const isHeroImage = this.isHeroImage(img);

          if (isHeroImage) {
            // Hero images: fade in immediately without stagger
            if (img.complete && img.naturalHeight !== 0) {
              img.classList.add('loaded');
            } else {
              img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
              img.addEventListener('error', () => img.classList.add('loaded'), { once: true });
            }
          } else {
            // Other visible images: fade in with stagger
            if (img.complete && img.naturalHeight !== 0) {
              this.fadeInImage(img);
            } else {
              img.addEventListener('load', () => this.fadeInImage(img), { once: true });
              img.addEventListener('error', () => this.fadeInImage(img), { once: true });
            }
          }
        } else {
          // Not yet visible - observe with Intersection Observer
          this.intersectionObserver.observe(img);
        }
      }
    });
  }

  isHeroImage(img) {
    // Check if this is a main product image or hero image
    // These should appear instantly without fade for optimal UX
    const parent = img.closest('[data-main-product], .product-media-gallery, .hero');
    if (!parent) return false;

    // Check if it's the first/main image
    const allImages = parent.querySelectorAll('img');
    return allImages[0] === img || img.hasAttribute('data-main-image');
  }

  fadeInImage(img) {
    // Calculate stagger delay based on visibility order
    const delay = this.visibleCount * this.staggerDelay;
    this.visibleCount++;

    setTimeout(() => {
      img.classList.add('loaded');
    }, delay);
  }

  observeNewContent() {
    // Use MutationObserver to detect dynamically added images
    const mutationObserver = new MutationObserver((mutations) => {
      let hasNewImages = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is an image matching our selectors
            if (node.tagName === 'IMG') {
              for (const selector of this.imageSelectors) {
                if (node.matches(selector)) {
                  hasNewImages = true;
                  break;
                }
              }
            }

            // Check if the node contains images matching our selectors
            if (node.querySelectorAll) {
              for (const selector of this.imageSelectors) {
                if (node.querySelectorAll(selector).length > 0) {
                  hasNewImages = true;
                  break;
                }
              }
            }
          }
        });
      });

      if (hasNewImages) {
        // Small delay to ensure DOM is settled
        setTimeout(() => this.observeImages(), 50);
      }
    });

    // Observe the entire document for changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  listenForVariantChanges() {
    // Listen for variant:update events (fired after variant picker morph completes)
    document.addEventListener('variant:update', () => {
      // Re-observe images after variant change (product media gallery may have updated)
      setTimeout(() => this.observeImages(), 100);
    });
  }
}

// Initialize when script loads
new LazyLoadFade();
